/**
 * Shared locale-aware formatters for currency, percent, and numbers.
 *
 * Uses `Intl.NumberFormat('fr-CA' | 'en-CA', ...)` — the browser handles
 * grouping/decimal/currency suffix natively, so we never manipulate
 * strings.  FR-CA emits currency as suffix (`420 000 $`); EN-CA emits
 * currency as prefix (`$420,000`).
 *
 * Reused by Compare.js and PortfolioCompare.js so both pages format the
 * same way with zero duplication.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default function useFormatters() {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const locale = isFr ? 'fr-CA' : 'en-CA';
  return useMemo(() => ({
    money0: (v) => new Intl.NumberFormat(locale, {
      style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
    }).format(Number(v) || 0),
    money2: (v) => new Intl.NumberFormat(locale, {
      style: 'currency', currency: 'CAD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v) || 0),
    percent: (v, d = 2) => new Intl.NumberFormat(locale, {
      minimumFractionDigits: d, maximumFractionDigits: d,
    }).format(Number(v) || 0) + (isFr ? ' %' : '%'),
    number: (v) => new Intl.NumberFormat(locale).format(Number(v) || 0),
    isFr,
  }), [locale, isFr]);
}
